// features/employee/fund/hook/useEmployeeFund.js
import { useForm } from "antd/es/form/Form";
import { message, Modal } from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { generateRandomString } from "utils/helper";
import { Alert } from "components/elements";
import * as fundService from "features/fund/services/fundApi";
import * as service from "../services/employeeFundApi";
import { DEFAULT_MEMBERSHIP_VALUES } from "../schemas/EmployeeFundSchemas";

const currentBuddhistYear = () => dayjs().year();

function useEmployeeFund() {
    const { t, i18n } = useTranslation();
    const { id: employeeId } = useParams(); // employee id from the parent /employee/:id/... route
    const currentCompany = useSelector((state) => state.company.currentCompany);
    const [form] = useForm();

    const normalizeId = useCallback((value) => {
        if (value == null) return undefined;
        if (typeof value === "string" || typeof value === "number") return value;
        return value?.$oid ?? value?.toString?.() ?? value;
    }, []);

    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [membershipList, setMembershipList] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, pageSize: 10, total: 0 });
    const [summary, setSummary] = useState();
    const [year, setYear] = useState(currentBuddhistYear());
    const [fundOptions, setFundOptions] = useState([]);

    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState("create");
    const [selectedId, setSelectedId] = useState();

    // ---------- fund master-data options for the "ชื่อกองทุน" dropdown ----------

    const fetchFundOptions = useCallback(async () => {
        try {
            const response = await fundService.getFundList({
                requestId: generateRandomString(),
                companyId: currentCompany,
                page: 0,
                size: 200,
            });
            const list = response?.data?.content || [];
            console.log("list", list);

            setFundOptions(
                list.map((f) => ({
                    value: f.id,
                    label: i18n.language === "en" ? f.fundNameEn : f.fundNameTh,
                    fundCode: f.fundCode,
                    securitiesCompany: f.securitiesCompany,
                    ...f
                }))
            );
        } catch (error) {
            setFundOptions([]);
        }
    }, [currentCompany, i18n.language]);

    useEffect(() => {
        fetchFundOptions();
    }, [fetchFundOptions]);

    // ---------- list ----------

    const mapRecord = useCallback(
        (m) => ({
            ...m,
            contractStartDate: m.contractStartDate ? dayjs(m.contractStartDate).format("DD/MM/YYYY") : "-",
            contributionEligibleSince: m.contributionEligibleSince
                ? dayjs(m.contributionEligibleSince).format("DD/MM/YYYY")
                : null,
        }),
        []
    );

    const fetchMembershipList = useCallback(
        async (page = pagination.page, pageSize = pagination.pageSize) => {
            if (!employeeId) return;
            try {
                setIsLoading(true);
                const response = await service.getMembershipList(employeeId, { page: page - 1, size: pageSize });
                const content = response?.data?.content || [];
                setMembershipList(content.map(mapRecord));
                setPagination({
                    page,
                    pageSize,
                    total: response?.data?.totalElements ?? content.length,
                });
            } catch (error) {
                Alert({ type: "error", resultObject: error });
            } finally {
                setIsLoading(false);
            }
        },
        [employeeId, pagination.page, pagination.pageSize, mapRecord]
    );

    const fetchSummary = useCallback(
        async (targetYear = year) => {
            if (!employeeId) return;
            try {
                const response = await service.getMembershipSummary(employeeId, targetYear);
                setSummary({
                    ...response?.data,
                    asOfDate: response?.data?.asOfDate ? dayjs(response.data.asOfDate).format("DD/MM/YYYY") : null,
                });
            } catch (error) {
                setSummary(undefined);
            }
        },
        [employeeId, year]
    );

    useEffect(() => {
        if (employeeId) {
            fetchMembershipList(1, pagination.pageSize);
            fetchSummary(year);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employeeId]);

    const onYearChange = useCallback(
        (newYear) => {
            setYear(newYear);
            fetchSummary(newYear);
        },
        [fetchSummary]
    );

    const onChange = useCallback(
        (tablePagination) => {
            fetchMembershipList(tablePagination.current, tablePagination.pageSize);
        },
        [fetchMembershipList]
    );

    // ---------- modal open/close ----------

    const openCreateModal = useCallback(() => {
        form.resetFields();
        form.setFieldsValue(DEFAULT_MEMBERSHIP_VALUES);
        setSelectedId(undefined);
        setModalMode("create");
        setModalOpen(true);
    }, [form]);

    const loadRecordIntoForm = useCallback(
        async (record, mode) => {
            try {
                setIsLoading(true);
                const response = await service.getMembershipById(employeeId, record.id);
                const data = response?.data || {};

                // Keep the form shape independent from whether the API returns
                // fundId/fundCode directly or also embeds a fund object.
                const fund = data.fund || data.fundDetail || {};
                const fundId =
                    data.fundId ??
                    fund.id ??
                    fund._id?.$oid ??
                    fund._id ??
                    fund.$id;

                const normalizedFundId = normalizeId(fundId);

                const fundCode =
                    data.fundCode ??
                    fund.fundCode ??
                    fund.code ??
                    "";

                const securitiesCompany =
                    data.securitiesCompany ??
                    fund.securitiesCompany ??
                    fund.securitiesCompanyName ??
                    "";

                const membershipEligibility = data?.membershipEligibility
                    ? {
                        ...data.membershipEligibility,
                        effectiveStartDate: data.membershipEligibility?.effectiveStartDate
                            ? dayjs(data.membershipEligibility?.effectiveStartDate)
                            : undefined,
                        duration: (Math?.abs((data.membershipEligibility?.effectiveStartDate
                            ? dayjs(data.membershipEligibility?.effectiveStartDate)
                            : undefined)?.diff(dayjs(), data?.membershipEligibility?.durationUnit == 'YEAR' ? 'year' : 'month')) || 0),
                    }
                    : undefined;

                const contributionEligibility = data.contributionEligibility
                    ? {
                        ...data.contributionEligibility,
                        effectiveStartDate: data?.contributionEligibility?.effectiveStartDate
                            ? dayjs(data?.contributionEligibility?.effectiveStartDate)
                            : undefined,
                        duration: (Math?.abs((data.contributionEligibility?.effectiveStartDate
                            ? dayjs(data.contributionEligibility?.effectiveStartDate)
                            : undefined)?.diff(dayjs(), data?.contributionEligibility?.durationUnit == 'YEAR' ? 'year' : 'month')) || 0),
                    }
                    : undefined;

                form.resetFields();
                form.setFieldsValue({
                    ...data,
                    fundId: normalizedFundId,
                    fundCodeDisplay: fundCode,
                    securitiesCompanyDisplay: securitiesCompany,
                    contractStartDate: data.contractStartDate
                        ? dayjs(data.contractStartDate)
                        : undefined,
                    contractEndDate: data.contractEndDate
                        ? dayjs(data.contractEndDate)
                        : undefined,
                    statusFlag: data.statusFlag ?? "ACTIVE",
                    membershipEligibility,
                    contributionEligibility,
                    beneficiaries: Array.isArray(data.beneficiaries)
                        ? data.beneficiaries
                        : [],
                });
                setSelectedId(record.id);
                setModalMode(mode);
                setModalOpen(true);
            } catch (error) {
                Alert({ type: "error", resultObject: error });
            } finally {
                setIsLoading(false);
            }
        },
        [employeeId, form, normalizeId]
    );

    const onAdd = useCallback(() => openCreateModal(), [openCreateModal]);
    const onView = useCallback((record) => loadRecordIntoForm(record, "view"), [loadRecordIntoForm]);
    const onEdit = useCallback((record) => loadRecordIntoForm(record, "edit"), [loadRecordIntoForm]);

    const onDelete = useCallback(
        (record) => {
            Modal.confirm({
                title: t("dialog.confirmation.header"),
                content: t("employeeFund.confirm.delete") || "ยืนยันการลบกองทุนนี้ออกจากพนักงาน?",
                okText: t("common.confirm"),
                cancelText: t("common.cancel"),
                onOk: async () => {
                    try {
                        await service.deleteMembership(employeeId, record.id);
                        message.success(t("common.deleteSuccess") || "ลบสำเร็จ");
                        fetchMembershipList(1, pagination.pageSize);
                        fetchSummary(year);
                    } catch (error) {
                        Alert({ type: "error", resultObject: error });
                    }
                },
            });
        },
        [employeeId, t, fetchMembershipList, fetchSummary, pagination.pageSize, year]
    );

    const onModalCancel = useCallback(() => {
        setModalOpen(false);
    }, []);

    const onModalOk = useCallback(async (v) => {
        console.log("onModalOk", modalMode);

        if (modalMode === "view") {
            setModalOpen(false);
            return;
        }
        try {
            const values = await form.getFieldsValue(true);
            console.log("values", values);

            setIsSaving(true);

            // strip UI-only display fields and format dates for the API
            const payload = {
                fundId: normalizeId(values.fundId),
                contractStartDate: values.contractStartDate?.format("YYYY-MM-DD"),
                contractEndDate: values.contractEndDate?.format("YYYY-MM-DD") || null,
                statusFlag: values.statusFlag ?? "ACTIVE",
                membershipEligibility: values.membershipEligibility
                    ? {
                        ...values.membershipEligibility,
                        effectiveStartDate:
                            values.membershipEligibility.effectiveStartDate?.format("YYYY-MM-DD"),
                    }
                    : null,
                contributionEligibility: values.contributionEligibility
                    ? {
                        ...values.contributionEligibility,
                        effectiveStartDate:
                            values.contributionEligibility.effectiveStartDate?.format("YYYY-MM-DD"),
                    }
                    : null,
                employeeContribution: values.employeeContribution,
                companyContribution: values.companyContribution,
                beneficiaries: values.beneficiaries || [],
            };
            console.log("modalMode", modalMode);

            if (modalMode === "edit" && selectedId) {
                await service.updateMembership(employeeId, selectedId, payload);
                message.success(t("common.success") || "บันทึกสำเร็จ");
            } else {
                await service.createMembership(employeeId, payload);
                message.success(t("common.success") || "เพิ่มกองทุนสำเร็จ");
            }

            setModalOpen(false);
            fetchMembershipList(1, pagination.pageSize);
            fetchSummary(year);
        } catch (error) {
            console.log("error", error);

            if (error?.errorFields) return; // antd validation error — already shown inline
            Alert({ type: "error", resultObject: error });
        } finally {
            setIsSaving(false);
        }
    }, [modalMode, selectedId, employeeId, form, fetchMembershipList, fetchSummary, pagination.pageSize, year, t, normalizeId]);

    return {
        isLoading,
        isSaving,
        membershipList,
        pagination,
        summary,
        year,
        currentBuddhistYear: currentBuddhistYear(),
        onYearChange,
        onChange,
        onAdd,
        onView,
        onEdit,
        onDelete,
        modalOpen,
        modalMode,
        form,
        fundOptions,
        onModalOk,
        onModalCancel,
    };
}

export default useEmployeeFund;
